import DropdownArrowIcon from '@/assets/icons/DropdownArrowIcon';
import React, { useState, useRef, useEffect } from 'react';

interface DropdownProps {
  options: { title: string; slug: string; categoryId: string }[];
  placeholder?: string;
  value?: { title: string; slug: string };
  onChange: (title: string, slug: string, categoryId: string) => void;
  className?: string;
  dropDownType: 'default' | 'lead';
  label: string;
}

const Dropdown: React.FC<DropdownProps> = ({
  options,
  placeholder = 'Select an option',
  value,
  onChange,
  className = '',
  dropDownType = 'default',
  label,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, []);

  const toggleDropdown = () => {
    setIsOpen((prev) => !prev);
  };

  const handleSelect = (title: string, slug: string, categoryId: string) => {
    onChange(title, slug, categoryId);
    setIsOpen(false);
  };

  const selectedLabel = options.find((option) => option.slug === value?.slug)?.title;

  return (
    <div ref={dropdownRef} className={`relative w-full cursor-pointer ${className}`}>
      {/* Dropdown Trigger */}
      <div className="flex flex-col gap-3">
        {label && <p className="text-[16px] text-gray-800 font-nunito font-normal">{label}</p>}
        <div
          className={`p-3 pl-4 medium:!p-[10px] large:!p-[10px] bg-white flex justify-between items-center ${
            dropDownType === 'lead' ? 'border-b-2' : 'border rounded-[4px] medium:rounded-lg'
          } ${isOpen ? 'border-gray-700' : 'border-gray-200'}`}
          onClick={toggleDropdown}
        >
          <span
            className={
              selectedLabel
                ? 'text-gray-800 font-nunito text-[14px] font-semibold'
                : 'text-gray-400 font-nunito text-[14px] font-semibold'
            }
          >
            {selectedLabel || placeholder}
          </span>
          <DropdownArrowIcon
            className={`w-5 h-5 transform transition-transform ${
              isOpen ? 'rotate-180 text-gray-700' : 'text-gray-700'
            }`}
          />
        </div>
      </div>

      {isOpen && (
        <ul
          className="absolute top-full left-0 mt-1 w-full bg-white border border-gray-300 rounded shadow-lg max-h-24 medium:max-h-32 large:!max-h-60 overflow-y-auto z-10"
          role="listbox"
        >
          {options.map((option) => (
            <li
              key={option.slug}
              className={`p-3 pl-4 medium:p-[10px] large:!p-[10px] font-nunito text-[14px] font-semibold hover:bg-gray-100 ${
                value?.slug === option.slug ? 'bg-blue-50' : ''
              }`}
              role="option"
              tabIndex={0}
              aria-selected={value?.slug === option.slug}
              onClick={() => handleSelect(option.title, option.slug, option.categoryId)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  handleSelect(option.title, option.slug, option.categoryId);
                }
              }}
            >
              {option.title}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default Dropdown;
